import { Accordion, Badge, Box, Button, Card, Group, Paper, Stack, Text } from '@mantine/core';
import { JsonEditor } from 'json-edit-react';

import { formatDate } from '../request-logger.utils';

import type { TestStep } from '@admin/hooks/request-logger/useTestGenerator';

interface StepCardProps {
  step: TestStep;
  stepIndex: number;
  onRequestClick?: (requestId: string) => void;
}

export const StepCard = ({ step, onRequestClick }: StepCardProps) => {
  return (
    <Card shadow="xs" padding="md" withBorder>
      <Stack gap="sm">
        <Group justify="space-between">
          <Group gap="xs">
            <Badge color="blue" size="sm">
              {step.request.method}
            </Badge>
            <Text size="sm" fw={500} style={{ fontFamily: 'monospace' }}>
              {step.request.endpoint}
            </Text>
          </Group>
          <Badge color="gray" size="sm" variant="light">
            {step.response.status}
          </Badge>
        </Group>

        <Group gap="md">
          <Text size="xs" c="dimmed">
            {formatDate(step.timestamp)}
          </Text>
          {step.duration && (
            <Text size="xs" c="dimmed">
              Duration: {step.duration}ms
            </Text>
          )}
        </Group>

        <Accordion>
          {step.request.body && (
            <Accordion.Item value="request">
              <Accordion.Control>Request Body</Accordion.Control>
              <Accordion.Panel>
                <Box style={{ maxHeight: '300px', overflow: 'auto' }}>
                  <JsonEditor data={step.request.body} restrictEdit restrictDelete restrictAdd restrictDrag collapse={2} minWidth="100%" />
                </Box>
              </Accordion.Panel>
            </Accordion.Item>
          )}

          {step.response.body && (
            <Accordion.Item value="response">
              <Accordion.Control>Response Body</Accordion.Control>
              <Accordion.Panel>
                <Box style={{ maxHeight: '300px', overflow: 'auto' }}>
                  <JsonEditor data={step.response.body} restrictEdit restrictDelete restrictAdd restrictDrag collapse={2} minWidth="100%" />
                </Box>
              </Accordion.Panel>
            </Accordion.Item>
          )}

          {step.databaseChanges.length > 0 && (
            <Accordion.Item value="db-changes">
              <Accordion.Control>Database Changes ({step.databaseChanges.length})</Accordion.Control>
              <Accordion.Panel>
                <Stack gap="xs">
                  {step.databaseChanges.map((change, changeIndex) => {
                    const hasFields = change.changes && Object.keys(change.changes).length > 0;

                    return (
                      <Paper key={changeIndex} p="xs" withBorder>
                        <Group gap="xs">
                          <Badge size="xs" color={change.operation === 'insert' ? 'green' : change.operation === 'update' ? 'blue' : 'red'}>
                            {change.operation}
                          </Badge>
                          <Text size="xs" fw={500}>
                            {change.collection}
                          </Text>
                          {change.documentId && (
                            <Text size="xs" c="dimmed" style={{ fontFamily: 'monospace' }}>
                              {change.documentId.substring(0, 8)}
                            </Text>
                          )}
                        </Group>
                        {hasFields && (
                          <Text size="xs" c="dimmed" mt="xs">
                            Fields: {Object.keys(change.changes).join(', ')}
                          </Text>
                        )}
                      </Paper>
                    );
                  })}
                </Stack>
              </Accordion.Panel>
            </Accordion.Item>
          )}
        </Accordion>

        {onRequestClick && (
          <Button size="xs" variant="subtle" onClick={() => onRequestClick(step.requestId)}>
            View Full Request Details
          </Button>
        )}
      </Stack>
    </Card>
  );
};
